import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPlacement, updatePlacement, fetchPlacement, fetchCandidates, fetchJobs, fetchCompanies } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CommissionSplitEditor } from './CommissionSplitEditor';

interface PlacementFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  placementId?: string;
  candidateId?: string;
  jobId?: string;
  companyId?: string;
  applicationId?: string;
}

interface CommissionSplit {
  userId: string;
  splitPercentage: number;
}

export function PlacementForm({
  open,
  onOpenChange,
  onSuccess,
  placementId,
  candidateId: initialCandidateId,
  jobId: initialJobId,
  companyId: initialCompanyId,
  applicationId,
}: PlacementFormProps) {
  const queryClient = useQueryClient();
  const isEditMode = !!placementId;

  const [candidateId, setCandidateId] = useState(initialCandidateId || '');
  const [jobId, setJobId] = useState(initialJobId || '');
  const [companyId, setCompanyId] = useState(initialCompanyId || '');
  const [startDate, setStartDate] = useState('');
  const [salary, setSalary] = useState('');
  const [salaryCurrency, setSalaryCurrency] = useState('ZAR');
  const [feeType, setFeeType] = useState('percentage');
  const [feePercentage, setFeePercentage] = useState('');
  const [feeAmount, setFeeAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [commissionSplits, setCommissionSplits] = useState<CommissionSplit[]>([]);

  const [candidateSearch, setCandidateSearch] = useState('');
  const [jobSearch, setJobSearch] = useState('');

  // Fetch existing placement data when in edit mode
  const { data: existingPlacement } = useQuery({
    queryKey: ['placement', placementId],
    queryFn: () => fetchPlacement(placementId!),
    enabled: open && isEditMode,
  });

  // Reset form when dialog opens (and pre-fill if editing)
  useEffect(() => {
    if (open) {
      if (isEditMode && existingPlacement) {
        setCandidateId(existingPlacement.candidateId || '');
        setJobId(existingPlacement.jobId || '');
        setCompanyId(existingPlacement.companyId || '');
        setStartDate(existingPlacement.startDate ? existingPlacement.startDate.split('T')[0] : '');
        setSalary(existingPlacement.salary != null ? String(existingPlacement.salary) : '');
        setSalaryCurrency(existingPlacement.salaryCurrency || 'ZAR');
        setFeeType(existingPlacement.feeType || 'percentage');
        setFeePercentage(existingPlacement.feePercentage != null ? String(existingPlacement.feePercentage) : '');
        setFeeAmount(existingPlacement.feeAmount != null ? String(existingPlacement.feeAmount) : '');
        setNotes(existingPlacement.notes || '');
        setCommissionSplits(
          existingPlacement.commissions?.map((c) => ({
            userId: c.userId,
            splitPercentage: c.splitPercentage,
          })) ?? [],
        );
      } else {
        setCandidateId(initialCandidateId || '');
        setJobId(initialJobId || '');
        setCompanyId(initialCompanyId || '');
        setStartDate('');
        setSalary('');
        setSalaryCurrency('ZAR');
        setFeeType('percentage');
        setFeePercentage('');
        setFeeAmount('');
        setNotes('');
        setCommissionSplits([]);
      }
      setCandidateSearch('');
      setJobSearch('');
    }
  }, [open, initialCandidateId, initialJobId, initialCompanyId, isEditMode, existingPlacement]);

  // Fetch candidates for dropdown
  const { data: candidatesData } = useQuery({
    queryKey: ['candidates-search', candidateSearch],
    queryFn: () => fetchCandidates({ search: candidateSearch || undefined, limit: 20 }),
    enabled: open,
  });

  // Fetch jobs for dropdown
  const { data: jobsData } = useQuery({
    queryKey: ['jobs-search', jobSearch],
    queryFn: () => fetchJobs({ search: jobSearch || undefined, limit: 20 }),
    enabled: open,
  });

  // Fetch companies for dropdown
  const { data: companies = [] } = useQuery({
    queryKey: ['companies-all'],
    queryFn: () => fetchCompanies(),
    enabled: open,
  });

  const candidates = candidatesData?.data ?? [];
  const jobs = jobsData?.data ?? [];

  // Auto-fill company when job changes
  useEffect(() => {
    if (jobId) {
      const selectedJob = jobs.find((j) => j.id === jobId);
      if (selectedJob?.company?.id) {
        setCompanyId(selectedJob.company.id);
      }
    }
  }, [jobId, jobs]);

  // Auto-calculate fee amount when salary or percentage changes
  const calculatedFeeAmount = useMemo(() => {
    if (feeType === 'percentage') {
      const salaryNum = parseFloat(salary) || 0;
      const pctNum = parseFloat(feePercentage) || 0;
      return (salaryNum * pctNum) / 100;
    }
    return parseFloat(feeAmount) || 0;
  }, [feeType, salary, feePercentage, feeAmount]);

  // Update feeAmount display when using percentage type
  useEffect(() => {
    if (feeType === 'percentage') {
      setFeeAmount(calculatedFeeAmount > 0 ? calculatedFeeAmount.toFixed(2) : '');
    }
  }, [calculatedFeeAmount, feeType]);

  const createMutation = useMutation({
    mutationFn: createPlacement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['placements'] });
      onSuccess();
    },
  });

  const editMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => updatePlacement(placementId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['placements'] });
      queryClient.invalidateQueries({ queryKey: ['placement', placementId] });
      onSuccess();
    },
  });

  const mutation = isEditMode ? editMutation : createMutation;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!candidateId || !jobId || !companyId) return;

    const validSplits = commissionSplits.filter((s) => s.userId && s.splitPercentage > 0);

    const payload = {
      candidateId,
      jobId,
      companyId,
      applicationId,
      startDate: startDate || undefined,
      salary: salary ? parseFloat(salary) : undefined,
      salaryCurrency,
      feeType,
      feePercentage: feePercentage ? parseFloat(feePercentage) : undefined,
      feeAmount: calculatedFeeAmount > 0 ? calculatedFeeAmount : undefined,
      notes: notes || undefined,
      commissions: validSplits.length > 0 ? validSplits : undefined,
    };

    if (isEditMode) {
      editMutation.mutate(payload as Record<string, unknown>);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Placement' : 'Create Placement'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Candidate Selection */}
          <div className="space-y-2">
            <Label htmlFor="candidate">Candidate *</Label>
            <Select value={candidateId} onValueChange={setCandidateId}>
              <SelectTrigger>
                <SelectValue placeholder="Select candidate" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input
                    placeholder="Search candidates..."
                    value={candidateSearch}
                    onChange={(e) => setCandidateSearch(e.target.value)}
                    className="mb-2"
                  />
                </div>
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                    {c.currentJobTitle ? ` - ${c.currentJobTitle}` : ''}
                  </SelectItem>
                ))}
                {candidates.length === 0 && (
                  <div className="p-2 text-sm text-text-tertiary text-center">
                    No candidates found
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Job Selection */}
          <div className="space-y-2">
            <Label htmlFor="job">Job *</Label>
            <Select value={jobId} onValueChange={setJobId}>
              <SelectTrigger>
                <SelectValue placeholder="Select job" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input
                    placeholder="Search jobs..."
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    className="mb-2"
                  />
                </div>
                {jobs.map((j) => (
                  <SelectItem key={j.id} value={j.id}>
                    {j.title}
                    {j.company ? ` (${j.company.name})` : ''}
                  </SelectItem>
                ))}
                {jobs.length === 0 && (
                  <div className="p-2 text-sm text-text-tertiary text-center">
                    No jobs found
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Company (auto-filled from job) */}
          <div className="space-y-2">
            <Label htmlFor="company">Company *</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger>
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {jobId && companyId && (
              <p className="text-xs text-text-tertiary">Auto-filled from selected job</p>
            )}
          </div>

          {/* Dates and Salary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="salary">Salary</Label>
              <div className="flex gap-2">
                <Input
                  id="salary"
                  type="number"
                  min="0"
                  step="1000"
                  placeholder="e.g. 500000"
                  value={salary}
                  onChange={(e) => setSalary(e.target.value)}
                  className="flex-1"
                />
                <Select value={salaryCurrency} onValueChange={setSalaryCurrency}>
                  <SelectTrigger className="w-[90px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ZAR">ZAR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Fee Details */}
          <div className="space-y-4">
            <Label>Fee Details</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="feeType" className="text-xs text-text-tertiary">
                  Fee Type
                </Label>
                <Select value={feeType} onValueChange={setFeeType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Fee</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {feeType === 'percentage' ? (
                <div className="space-y-2">
                  <Label htmlFor="feePercentage" className="text-xs text-text-tertiary">
                    Fee Percentage
                  </Label>
                  <div className="relative">
                    <Input
                      id="feePercentage"
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      placeholder="e.g. 15"
                      value={feePercentage}
                      onChange={(e) => setFeePercentage(e.target.value)}
                      className="pr-6"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-text-tertiary text-sm">
                      %
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="flatFeeAmount" className="text-xs text-text-tertiary">
                    Fee Amount
                  </Label>
                  <Input
                    id="flatFeeAmount"
                    type="number"
                    min="0"
                    step="100"
                    placeholder="e.g. 75000"
                    value={feeAmount}
                    onChange={(e) => setFeeAmount(e.target.value)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs text-text-tertiary">Calculated Fee</Label>
                <div className="h-10 flex items-center px-3 rounded-md border border-white/20 bg-bg-elevated text-emerald-500 font-semibold">
                  {calculatedFeeAmount > 0
                    ? new Intl.NumberFormat('en-ZA', {
                        style: 'currency',
                        currency: salaryCurrency,
                        minimumFractionDigits: 0,
                      }).format(calculatedFeeAmount)
                    : '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Commission Splits */}
          <div className="space-y-2">
            <Label>Commission Splits</Label>
            <CommissionSplitEditor
              splits={commissionSplits}
              onChange={setCommissionSplits}
              feeAmount={calculatedFeeAmount}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Any additional notes about this placement..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Footer */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!candidateId || !jobId || !companyId || mutation.isPending}
            >
              {mutation.isPending
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create Placement')}
            </Button>
          </DialogFooter>

          {mutation.isError && (
            <p className="text-sm text-red-500 mt-2">
              {(mutation.error as Error)?.message || (isEditMode ? 'Failed to update placement' : 'Failed to create placement')}
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
